import './App.css';
import { Box } from '@mui/material';
import Board from './Components/Board';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import Notifications from './Notifications';
import Chat from './Components/Chat';
import { useDispatch } from 'react-redux';
import { getAnalytics } from "firebase/analytics";
import { firebaseApp } from './firebase';
import { watchAccount } from './account/session';
import NameDialog from './account/NameDialog';
import MyGames from './Components/Pages/MyGames';
import { appendMessage, setAnalyticsInstance, setCallParticipants, setConnected, setGame, setMessages, setPresence } from './services/GameSlice';
import { setBody, setType } from './services/NotificationSlice';
import { getSocket } from './socket';
import ContentLayout from './Components/Layout/ContentLayout';
import HowToPlay from './Components/Pages/HowToPlay';
import Strategy from './Components/Pages/Strategy';
import About from './Components/Pages/About';
import FAQ from './Components/Pages/FAQ';
import PrivacyPolicy from './Components/Pages/PrivacyPolicy';
import TermsOfService from './Components/Pages/TermsOfService';
import Contact from './Components/Pages/Contact';
import Leaderboard from './Components/Pages/Leaderboard';
import Replay from './Components/Replay';
import Classic from './Components/Classic';
import { gamePath, prefersClassic } from './uiMode';
import { isPhoneNow } from './usePhone';
import Canonical from './Components/Layout/Canonical';
import PlayVsComputer from './Components/Pages/PlayVsComputer';
import PlayWithFriends from './Components/Pages/PlayWithFriends';
import HowManyPlayers from './Components/Pages/HowManyPlayers';
import DailyPuzzle from './Components/Daily/DailyPuzzle';
import { setStatsPage, startSiteStats, trackPage } from './analytics/siteStats';

// A game link opens in the look this browser last chose.
const Game = () => {
  const { gameId } = useParams();
  // The classic look has no phone layout, so phones always get the new one.
  if (prefersClassic() && !isPhoneNow()) {
    return <Navigate to={gamePath(gameId, true)} replace />;
  }
  return <><Chat /><Board /></>;
};

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    const analytics = getAnalytics(firebaseApp);
    dispatch(setAnalyticsInstance(analytics));
  }, [dispatch])

  // The site's own visitor counts, for the admin dashboard.
  const { pathname } = useLocation();
  useEffect(() => { startSiteStats(); }, []);
  useEffect(() => {
    setStatsPage(pathname);
    trackPage(pathname);
  }, [pathname]);

  // Whether someone is signed in on this browser, for the life of the app.
  useEffect(() => watchAccount(dispatch), [dispatch]);

  // Single place where server pushes are turned into store updates. Mounted
  // once for the whole app so listeners are never registered twice.
  useEffect(() => {
    const socket = getSocket();

    const onConnect = () => dispatch(setConnected(true));
    const onDisconnect = () => dispatch(setConnected(false));
    const onGameUpdate = (game) => dispatch(setGame(game));
    const onPresence = (presence) => dispatch(setPresence(presence));
    const onChatHistory = (messages) => dispatch(setMessages(messages));
    const onChatMessage = (message) => dispatch(appendMessage(message));
    const onCallParticipants = (call) => dispatch(setCallParticipants(call));
    const onError = (error) => {
      dispatch(setType('error'));
      dispatch(setBody(error?.message || 'Something went wrong'));
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('game:update', onGameUpdate);
    socket.on('game:presence', onPresence);
    socket.on('chat:history', onChatHistory);
    socket.on('chat:message', onChatMessage);
    socket.on('call:participants', onCallParticipants);
    socket.on('game:error', onError);

    if (socket.connected) {
      dispatch(setConnected(true));
    }

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('game:update', onGameUpdate);
      socket.off('game:presence', onPresence);
      socket.off('chat:history', onChatHistory);
      socket.off('chat:message', onChatMessage);
      socket.off('call:participants', onCallParticipants);
      socket.off('game:error', onError);
    };
  }, [dispatch])

  return (
    <Box>
      <Notifications />
      <NameDialog />
      <Canonical />
      <Routes>
        <Route path='/' element={<Game />} />
        <Route path='/r/:gameId' element={<Replay />} />
        <Route path='/classic' element={<Classic />} />
        <Route path='/classic/:gameId' element={<Classic />} />
        <Route path='/:gameId' element={<Game />} />
        <Route element={<ContentLayout />}>
          <Route path='/how-to-play' element={<HowToPlay />} />
          <Route path='/strategy' element={<Strategy />} />
          <Route path='/about' element={<About />} />
          <Route path='/faq' element={<FAQ />} />
          <Route path='/privacy-policy' element={<PrivacyPolicy />} />
          <Route path='/terms' element={<TermsOfService />} />
          <Route path='/contact' element={<Contact />} />
          <Route path='/leaderboard' element={<Leaderboard />} />
          <Route path='/play-against-computer' element={<PlayVsComputer />} />
          <Route path='/play-with-friends' element={<PlayWithFriends />} />
          <Route path='/how-many-players' element={<HowManyPlayers />} />
          <Route path='/daily' element={<DailyPuzzle />} />
          <Route path='/my-games' element={<MyGames />} />
        </Route>
      </Routes>
    </Box>
  );
}

export default App;
